import { Button, LoadingShim } from "@narmi/design_system";
import React from "react";

type OlbSentry = { captureException?: (error: unknown) => void };

/**
 * Catches what lending's own errorElement cannot: a failure loading the lazy chunk.
 *
 * The common cause is a deploy — the browser holds HTML referencing a bundle hash that no
 * longer exists, so the dynamic import rejects before any router exists to handle it.
 * Reloading fetches the current HTML and fixes it, which is why that is the offered action.
 */
class LendingErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  // eslint-disable-next-line class-methods-use-this -- React fixes the signature
  componentDidCatch(error: Error) {
    (window as { Sentry?: OlbSentry }).Sentry?.captureException?.(error);
  }

  render() {
    if (!this.state.failed) {
      return this.props.children;
    }

    return (
      <div className="padding--all--l alignChild--center--center">
        <div className="alignChild--center--center flex--column gap--l">
          <span>Sorry, we could not load this page.</span>
          <Button
            label="Reload"
            onClick={() => {
              window.location.reload();
            }}
          />
        </div>
      </div>
    );
  }
}

/** Shown while the lending chunk downloads. */
export function LendingLoading() {
  return (
    <LoadingShim isLoading>
      <div style={{ height: "60vh" }} />
    </LoadingShim>
  );
}

export default LendingErrorBoundary;
